// Pure audio-track logic for the JW player (CPACWEB-420).
//
// Everything in this file is side-effect-free (except probeMasterAudioTracks' fetch and
// audioDebug's console) and unit-tested in audio-tracks.test.ts. The stateful reconciliation
// loop that uses these helpers lives in audio-track-reconciler.ts (adapted to JW by
// CpacJwPlayerContainer.tsx); the architecture is
// documented in doc/video-player-audio-architecture.md.

/** The three audio languages CPAC streams carry. Anything unrecognized is treated as floor. */
export type AudioLang = "en" | "fr" | "floor";

/** An audio rendition as declared in the HLS master manifest (#EXT-X-MEDIA:TYPE=AUDIO). */
export type ManifestAudioTrack = {
  /** NAME attribute, e.g. "English" / "French" / "Floor". */
  name: string;
  /** Normalized language — drives buttons, matching, and messages. */
  lang: AudioLang;
  /** LANGUAGE attribute exactly as served; undefined when absent (Vualto's floor track). */
  rawLang?: string;
  /** DEFAULT=YES on the rendition. */
  isDefault: boolean;
};

/**
 * The shape of entries returned by jwplayer().getAudioTracks(). JW's providers differ
 * (hls provider vs native iOS), so every field is optional and matching is defensive.
 */
export type PlayerAudioTrack = {
  name?: string;
  language?: string;
  lang?: string;
};

/**
 * Normalize any language identifier a manifest, JW provider, or Safari might hand us.
 * Accepts ISO 639-1 ("en"), 639-2 ("eng"/"fre"/"fra"), BCP-47 ("en-CA"), and the
 * display names JW falls back to ("English"/"French"/"Floor"). Empty, undefined,
 * "mul", "und", and anything unrecognized all mean the untranslated floor feed —
 * matching the long-standing behaviour where unknown tracks are labelled FLOOR.
 */
export function normalizeAudioLang(raw: string | undefined | null): AudioLang {
  if (raw == null) return "floor";
  const v = raw.trim().toLowerCase();
  if (v === "en" || v === "eng" || v === "english" || v.startsWith("en-")) return "en";
  if (v === "fr" || v === "fre" || v === "fra" || v === "french" || v === "français" || v === "francais" || v.startsWith("fr-")) return "fr";
  return "floor";
}

/** Normalized language of a live player track: LANGUAGE-style fields first, NAME as fallback. */
export function playerTrackLang(track: PlayerAudioTrack): AudioLang {
  const raw = track.language ?? track.lang;
  if (raw != null && raw.trim() !== "") return normalizeAudioLang(raw);
  return normalizeAudioLang(track.name);
}

/**
 * Parse the #EXT-X-MEDIA:TYPE=AUDIO renditions out of a master m3u8, in manifest order,
 * deduplicated by normalized language (a manifest with several audio GROUP-IDs would
 * otherwise yield duplicate buttons; first occurrence wins).
 */
export function parseMasterAudioTracks(manifestText: string): ManifestAudioTrack[] {
  const tracks: ManifestAudioTrack[] = [];
  const seen = new Set<AudioLang>();
  for (const rawLine of manifestText.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line.startsWith("#EXT-X-MEDIA:")) continue;
    const attrs = parseAttributeList(line.substring("#EXT-X-MEDIA:".length));
    if (attrs["TYPE"] !== "AUDIO") continue;
    const rawLang = attrs["LANGUAGE"];
    const name = attrs["NAME"] ?? "";
    const lang = normalizeAudioLang(rawLang ?? name);
    if (seen.has(lang)) continue;
    seen.add(lang);
    tracks.push({ name, lang, rawLang, isDefault: attrs["DEFAULT"] === "YES" });
  }
  return tracks;
}

/** Attribute list per RFC 8216 §4.2: comma-separated KEY=value, values possibly quoted. */
function parseAttributeList(text: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /([A-Z0-9-]+)=("[^"]*"|[^,]*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const value = m[2];
    attrs[m[1]] = value.startsWith('"') ? value.slice(1, -1) : value;
  }
  return attrs;
}

/**
 * Fetch + parse the master manifest's audio renditions. Replaces the old hls.js probe on
 * every platform (identical CORS fetch, ~200 bytes of parsing instead of the whole library),
 * which also means iPhone gets the language buttons before playback starts. Returns [] on
 * any failure — callers must treat an empty result as "unknown", never as "no tracks".
 */
export async function probeMasterAudioTracks(url: string): Promise<ManifestAudioTrack[]> {
  try {
    // no-store: the live re-probe must see manifest changes, not a cached master.
    const resp = await fetch(url, { cache: "no-store" });
    if (!resp.ok) {
      audioDebug("manifest probe HTTP", resp.status, url);
      return [];
    }
    return parseMasterAudioTracks(await resp.text());
  } catch (e) {
    audioDebug("manifest probe failed", url, e);
    return [];
  }
}

/**
 * Pick the index of the track to switch to in the player's OWN getAudioTracks() list.
 * Exact language match first, floor as fallback, undefined when there is no sane target —
 * the caller must then do nothing (never setCurrentAudioTrack(undefined)).
 */
export function chooseTrackIndex(desired: AudioLang, playerTracks: PlayerAudioTrack[] | undefined | null): number | undefined {
  if (!playerTracks || playerTracks.length === 0) return undefined;
  const langs = playerTracks.map(playerTrackLang);
  const exact = langs.indexOf(desired);
  if (exact >= 0) return exact;
  const floor = langs.indexOf("floor");
  return floor >= 0 ? floor : undefined;
}

/** The distinct normalized languages present, sorted — the canonical "what audio exists" view. */
export function audioLangsOf(tracks: ManifestAudioTrack[]): AudioLang[] {
  return [...new Set(tracks.map((t) => t.lang))].sort();
}

/** Whether two track lists offer the same languages (order- and duplicate-insensitive). */
export function sameAudioLangs(a: ManifestAudioTrack[], b: ManifestAudioTrack[]): boolean {
  return audioLangsOf(a).join(",") === audioLangsOf(b).join(",");
}

/**
 * The "only the other language is available" notice: on the English page, a stream carrying
 * French but neither English nor floor audio warrants FRE_AUDIO_ONLY (and mirrored for the
 * French page). Returns the label key; the caller localizes it.
 */
export function audioAvailabilityMessageKey(
  tracks: ManifestAudioTrack[],
  pageLang: AudioLang,
): "FRE_AUDIO_ONLY" | "ENG_AUDIO_ONLY" | undefined {
  const langs = new Set(audioLangsOf(tracks));
  if (pageLang === "en" && langs.has("fr") && !langs.has("en") && !langs.has("floor")) return "FRE_AUDIO_ONLY";
  if (pageLang === "fr" && langs.has("en") && !langs.has("fr") && !langs.has("floor")) return "ENG_AUDIO_ONLY";
  return undefined;
}

/**
 * After a seek, a background→foreground, or right at startup, native players (AVFoundation)
 * may re-run their own audio auto-selection; a track change we did not command inside this
 * window is the platform fighting us and must be re-reconciled. Outside the window it is the
 * user picking a track in JW's gear menu, and their choice is adopted instead.
 */
export const TURBULENCE_WINDOW_MS = 3000;

export function classifyUncommandedChange(nowMs: number, lastTurbulenceMs: number | undefined): "platform" | "user" {
  if (lastTurbulenceMs !== undefined && nowMs - lastTurbulenceMs < TURBULENCE_WINDOW_MS) return "platform";
  return "user";
}

/**
 * STREAM-URL SEAM — the single place a playable stream URL is finalized per page language.
 * Both the JW playlist AND the manifest probe must consume this function's output, so the
 * button list always reflects the manifest actually served.
 *
 * v1 is a pass-through. Vualto (Unified Streaming) may support a URL parameter that sets
 * which rendition gets DEFAULT=YES in the served manifest; once that research lands
 * (CPACWEB-420 follow-up), wiring it in is a change to this function only.
 */
// _pageLang is deliberately unused until the Vualto parameter is confirmed.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function buildStreamUrl(url: string, _pageLang: AudioLang): string {
  return url;
}

/**
 * Verbose diagnostics, off by default: localStorage.setItem('cpacAudioDebug', '1') to enable.
 * The reconciler's from→to command log and GAVE UP warning are always on (they only fire when
 * a switch is actually issued); this gate is for the play-by-play trace.
 */
export function audioDebug(...args: unknown[]): void {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("cpacAudioDebug")) {
      console.log("[CPACWEB-420]", ...args);
    }
  } catch {
    // localStorage can throw in privacy modes — diagnostics must never break playback.
  }
}
